/* @flow */
import React, { PureComponent, Suspense } from 'react';
import { Layout } from 'antd';
import { Link } from 'react-router-dom';
import { RouterProps } from 'react-router';
import classNames from 'classnames';
import styles from './SideMenu.module.pcss';
import { Loader } from '@/components';
import { getDefaultCollapsedSubMenus } from './utils';
import { title } from '@/config/defaultConfig';

const BaseMenu = React.lazy(() => import('./BaseMenu'));
const { Sider } = Layout;

type Props = {
  menuItems: Array<MenuItemsT>,
  logo: string,
  isCollapsed: boolean,
  onCollapse: Function,
  isMobile: boolean,
  settings: SettingsT,
  flatMenuKeys: string[],
  router: RouterProps,
};

type State = {
  openKeys: string[],
  pathname?: string,
  flatMenuKeysLen?: number,
};

export default class SideMenu extends PureComponent<Props, State> {
  firstMount: false;

  constructor(props: Props) {
    super(props);
    this.state = {
      openKeys: getDefaultCollapsedSubMenus(props),
    };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const { pathname, flatMenuKeysLen } = state;
    const {
      router: { location = { pathname: '/manage/leads' } },
      flatMenuKeys = [],
    } = props;
    if (location.pathname !== pathname || flatMenuKeys.length !== flatMenuKeysLen) {
      return {
        pathname: location.pathname,
        flatMenuKeysLen: flatMenuKeys.length,
        openKeys: getDefaultCollapsedSubMenus(props),
      };
    }
    return null;
  }

  componentDidMount() {
    this.firstMount = false;
  }

  isMainMenu = (key: string) => {
    const { menuItems } = this.props;
    return menuItems.some((item) => {
      if (key) {
        return item.key === key || item.path === key;
      }
      return false;
    });
  };

  handleOpenChange = (openKeys: Array<string>) => {
    const moreThanOne = openKeys.filter((openKey) => this.isMainMenu(openKey)).length > 1;
    this.setState({
      openKeys: moreThanOne ? [openKeys.pop()] : [...openKeys],
    });
  };

  render() {
    const {
      logo,
      isCollapsed,
      onCollapse,
      isMobile,
      settings: { siderWidth, fixedSidebar },
    } = this.props;
    const { openKeys } = this.state;
    const defaultProps = isCollapsed ? {} : { openKeys };
    const siderClassName = classNames(styles.sider, {
      [styles.fixedSidebar]: fixedSidebar,
    });

    return (
      <Sider
        trigger={null}
        collapsible
        collapsedWidth={100}
        collapsed={isCollapsed}
        breakpoint="lg"
        onCollapse={(collapse) => {
          if (this.firstMount || !isMobile) {
            onCollapse(collapse);
          }
        }}
        width={siderWidth}
        className={siderClassName}
      >
        <div className={styles.logo} id="logo">
          <Link to="/">
            <img src={logo} alt="logo" />
            <h1>{title}</h1>
          </Link>
        </div>
        <Suspense fallback={<Loader size="default" />}>
          <BaseMenu
            {...this.props}
            mode="inline"
            handleOpenChange={this.handleOpenChange}
            style={{ padding: '16px 8px', width: '100%' }}
            {...defaultProps}
          />
        </Suspense>
      </Sider>
    );
  }
}
